import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { connect } from 'react-redux';
import {
  X,
  Calendar,
  FileText,
  Link as LinkIcon,
  Upload,
  Save,
  FolderOpen,
  File as FileIcon,
  FileSpreadsheet,
  Image,
  Info,
  Download
} from 'lucide-react';
import styles from './NewSubProjectModalV2.module.scss';
import { NewSubProject } from '../../../types/newProject';
import { newProjectServiceUtils } from '../../../services/newProjectService.ts';
import RubroSelector from '../RubroSelector/RubroSelector.tsx';
import { openMainModal, closeMainModal } from '../../../store/actions/profileActions';
import ModalOverlay from '../ModalOverlay/ModalOverlay.tsx';

interface NewSubProjectModalV2Props {
  isOpen: boolean;
  subProject: NewSubProject | null;
  onClose: () => void;
  onSave: (subProject: NewSubProject) => void;
  projectStartDate?: string;
  projectEndDate?: string;
  isLoading?: boolean;
  isCreatingNew?: boolean; 
  existingSubProjects?: NewSubProject[]; 
  // Redux props
  openMainModal: (modalData: any) => void;
  closeMainModal: () => void;
}

const NewSubProjectModalV2: React.FC<NewSubProjectModalV2Props> = ({
  isOpen,
  subProject,
  onClose,
  onSave,
  projectStartDate,
  projectEndDate,
  isLoading = false,
  isCreatingNew = false,
  existingSubProjects = [],
  openMainModal,
  closeMainModal
}) => {
  const [formData, setFormData] = useState<NewSubProject>({
    id: '',
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    link: '',
    computoMetrico: null,
    especificacionesTecnicas: null,
    plantillaDePresupuesto: null,
    planosArchivo: null,
    planosFile: null,
    isNew: true
  });

  const [validation, setValidation] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [selectedRubro, setSelectedRubro] = useState<string>("");
  // Función para obtener el icono según el tipo de archivo
  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FileIcon size={16} className={styles.fileIcon} />;
      case 'xlsx':
      case 'xls':
        return <FileSpreadsheet size={16} className={styles.fileIcon} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <Image size={16} className={styles.fileIcon} />;
      default:
        return <FileIcon size={16} className={styles.fileIcon} />;
    }
  };

  // Función para validar URL
  const validateUrl = (url: string): boolean => {
    if (!url.trim()) return true; // URL vacía es válida (opcional)
    
    try {
      const urlObj = new URL(url);
      // Verificar que sea HTTP o HTTPS
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const Tooltip = ({ content, children }: { content: string; children: React.ReactNode }) => (
    <div className={styles.tooltipContainer}>
      {children}
      <div className={styles.tooltipTriggerWrapper}>
        <button type="button" className={styles.tooltipTrigger} aria-label="Más información">
          <Info size={14} />
        </button>
        <div className={styles.tooltip} role="tooltip">
          {content}
        </div>
      </div>
    </div>
  );

  // Convertir fecha ISO a formato YYYY-MM-DD para inputs
  const formatDateForInput = useCallback((dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }, []);

  // Inicializar formulario cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      if (subProject) {
        // Editando subproyecto existente
        setFormData({
          ...subProject,
          // Formatear fechas para inputs
          startDate: formatDateForInput(subProject.startDate || ''),
          endDate: formatDateForInput(subProject.endDate || ''),
          // Archivos nuevos (File objects)
          plantillaDePresupuesto: subProject.plantillaDePresupuesto || null,
          planosArchivo: subProject.planosArchivo || null,
          planosFile: subProject.planosFile || null,
          computoMetrico: subProject.computoMetrico || null,
          especificacionesTecnicas: subProject.especificacionesTecnicas || null,
          // Archivos existentes (con URL) - se mantienen para mostrar
          existingPlanos: subProject.existingPlanos || null,
          existingComputoMetrico: subProject.existingComputoMetrico || null,
          existingEspecificacionesTecnicas: subProject.existingEspecificacionesTecnicas || null,
          existingPresupuesto: subProject.existingPresupuesto || null,
          isNew: false // Asegurar que se mantenga como existente
        });
        setSelectedRubro(subProject.name || "");
      } else {
        // Creando nuevo subproyecto
        setFormData({
          id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: '',
          description: '',
          startDate: formatDateForInput(projectStartDate || ''),
          endDate: formatDateForInput(projectEndDate || ''),
          link: '',
          computoMetrico: null,
          especificacionesTecnicas: null,
          plantillaDePresupuesto: null,
          planosArchivo: null,
          planosFile: null,
          existingPlanos: null,
          existingComputoMetrico: null,
          existingEspecificacionesTecnicas: null,
          existingPresupuesto: null,
          isNew: true
        });
        setSelectedRubro("");
      }
      setIsDirty(false);
      setValidation({});
    }
  }, [isOpen, subProject, projectStartDate, projectEndDate, formatDateForInput]);

  // Obtener fecha mínima
  const getMinDate = useCallback((): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Manejar cambios en inputs
  const handleInputChange = useCallback((field: keyof NewSubProject, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
    
    // Validar URL si es el campo link
    if (field === 'link' && value.trim()) {
      const isValidUrl = validateUrl(value);
      if (!isValidUrl) {
        setValidation(prev => ({ ...prev, [field]: 'Por favor ingresa una URL válida (ej: https://drive.google.com/...)' }));
        return;
      }
    }
    
    // Limpiar error específico
    if (validation[field]) {
      setValidation(prev => ({ ...prev, [field]: '' }));
    }
  }, [validation]);

  // Manejar selección de rubro
  const handleRubroSelect = useCallback((rubroId: number, rubroName: string) => {
    setFormData(prev => ({
      ...prev,
      name: rubroName,
      rubroId: rubroId
    }));
    setSelectedRubro(rubroName);
    setIsDirty(true);
    
    // Limpiar error de nombre si existe
    if (validation.name) {
      setValidation(prev => ({ ...prev, name: '' }));
    }
  }, [validation.name]);

  const handleRubroClear = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      name: '',
      rubroId: undefined
    }));
    setSelectedRubro("");
    setIsDirty(true);
  }, []);

  // Manejar archivos
  const handleFileChange = useCallback(async (
    field: 'plantillaDePresupuesto' | 'planosArchivo' | 'planosFile' | 'computoMetrico' | 'especificacionesTecnicas',
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    

    // Validar archivo
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/png'
    ];
    
    const sizeValid = newProjectServiceUtils.validateFileSize(file, 10 * 1024 * 1024);
    const typeValid = newProjectServiceUtils.validateFileType(file, allowedTypes);
    
    
    const validation = {
      isValid: sizeValid && typeValid,
      error: !sizeValid ? 'El archivo es demasiado grande' :
             !typeValid ? 'Tipo de archivo no permitido' : undefined
    };

    if (!validation.isValid) {
      setValidation(prev => ({ ...prev, [field]: validation.error || 'Archivo inválido' }));
      return;
    }

    setFormData(prev => ({ ...prev, [field]: file }));
    setIsDirty(true);
    
    // Limpiar error
    if (validation[field]) {
      setValidation(prev => ({ ...prev, [field]: '' }));
    }
  }, []);

  // Validar formulario
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    
    // Validar nombre
    if (!formData.name.trim()) {
      errors.name = 'El nombre es requerido';
    } else if (formData.name.length < 3) {
      errors.name = 'El nombre debe tener al menos 3 caracteres';
    }
    
    // Validar descripción
    if (!formData.description.trim()) {
      errors.description = 'La descripción es requerida';
    } else if (formData.description.length < 10) {
      errors.description = 'La descripción debe tener al menos 10 caracteres';
    }
    
    // Validar fechas
    if (!formData.startDate) {
      errors.startDate = 'La fecha de inicio es requerida';
    }
    
    if (!formData.endDate) {
      errors.endDate = 'La fecha de finalización es requerida';
    }
    
    if (formData.startDate && formData.endDate) {
      // Validar que la fecha de inicio no sea anterior a la del proyecto
      if (projectStartDate && new Date(formData.startDate) < new Date(projectStartDate)) {
        errors.startDate = 'La fecha de inicio no puede ser anterior a la del proyecto principal';
      }
      
      // Validar que la fecha de finalización no sea posterior a la del proyecto
      if (projectEndDate && new Date(formData.endDate) > new Date(projectEndDate)) {
        errors.endDate = 'La fecha de finalización no puede ser posterior a la del proyecto principal';
      }
      
      // Validar que la fecha de inicio no sea posterior a la de finalización del subproyecto
      if (new Date(formData.startDate) > new Date(formData.endDate)) {
        errors.endDate = 'La fecha de finalización debe ser posterior a la fecha de inicio';
      }
    }
    
    setValidation(errors);
    return Object.keys(errors).length === 0;
  }, [formData, projectStartDate, projectEndDate]);

  // Guardar subproyecto
  const handleSave = useCallback(() => {
    if (!validateForm()) return;
    
    onSave(formData);
  }, [formData, validateForm, onSave]);

  // Cerrar modal
  const handleClose = useCallback(() => {
    if (isDirty) {
      openMainModal({
        title: '¿Estás seguro de que quieres cerrar?',
        iconType: 'warning',
        body: 'Se perderán los cambios no guardados.',
        confirmHandler: () => {
          onClose();
          closeMainModal();
        }
      });
      return;
    }
    onClose();
  }, [isDirty, onClose, openMainModal, closeMainModal]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, handleClose]);

  return (
    <ModalOverlay
      isOpen={isOpen}
      onClose={handleClose}
      zIndex={3}
    >
      <motion.div
        className={styles.modal}
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
      >
          {/* Header */}
          <div className={styles.header}>
            <h3 className={styles.title}>
              {isCreatingNew ? 'Nuevo Rubro' : 'Editar Rubro'}
            </h3>
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              disabled={isLoading}
            >
              <X size={20} />
            </button>
          </div>

          {/* Formulario */}
          <div className={styles.content}>
            <div className={styles.form}>
              {/* Selector de Rubro */}
              <div className={styles.fieldGroup}>
                <label className={styles.label}>
                  <FileText size={18} />
                  Seleccionar Rubro *
                </label>
                <RubroSelector
                  onRubroSelect={handleRubroSelect}
                  existingRubros={existingSubProjects.filter(sub => sub.id !== formData.id)}
                  selectedRubro={selectedRubro}
                  onClear={handleRubroClear}
                  disabled={isLoading}
                />
                {validation.name && (
                  <span className={styles.errorText}>{validation.name}</span>
                )}
              </div>

              {/* Descripción */}
              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="subProjectDescription">
                  <FileText size={18} />
                  Descripción *
                </label>
                <textarea
                  id="subProjectDescription"
                  className={`${styles.textarea} ${validation.description ? styles.inputError : ''}`}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe detalladamente el trabajo a realizar..."
                  rows={4}
                  disabled={isLoading}
                />
                {validation.description && (
                  <span className={styles.errorText}>{validation.description}</span>
                )}
              </div>

              {/* Fechas */}
              <div className={styles.dateFields}>
                <div className={styles.fieldGroup}>
                  <Tooltip content="📅 Fecha desde la cual los profesionales pueden comenzar a enviar sus cotizaciones para este rubro. Esto te permite escalonar la recepción de presupuestos.">
                    <label className={styles.label} htmlFor="subProjectStartDate">
                      <Calendar size={18} />
                      Fecha de Inicio *
                    </label>
                  </Tooltip>
                  <input
                    id="subProjectStartDate"
                    type="date"
                    className={`${styles.input} ${validation.startDate ? styles.inputError : ''}`}
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    min={formatDateForInput(projectStartDate || '') || getMinDate()}
                    max={formatDateForInput(projectEndDate || '')}
                    disabled={isLoading}
                  />
                  {validation.startDate && (
                    <span className={styles.errorText}>{validation.startDate}</span>
                  )}
                </div>

                <div className={styles.fieldGroup}>
                  <Tooltip content="📅 Fecha límite para recibir cotizaciones de este rubro. Después de esta fecha, los profesionales ya no podrán enviar presupuestos para este trabajo específico.">
                    <label className={styles.label} htmlFor="subProjectEndDate">
                      <Calendar size={18} />
                      Fecha de Finalización *
                    </label>
                  </Tooltip>
                  <input
                    id="subProjectEndDate"
                    type="date"
                    className={`${styles.input} ${validation.endDate ? styles.inputError : ''}`}
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    min={formData.startDate || formatDateForInput(projectStartDate || '') || getMinDate()}
                    max={formatDateForInput(projectEndDate || '')}
                    disabled={isLoading}
                  />
                  {validation.endDate && (
                    <span className={styles.errorText}>{validation.endDate}</span>
                  )}
                </div>
              </div>

              {/* Opciones de documentación */}
              <div className={styles.documentationOptions}>
                <h4 className={styles.optionsTitle}>
                  Opciones para compartir documentación
                </h4>
                
                {/* Opción 1: Link de carpeta */}
                <div className={styles.optionCard}>
                  <div className={styles.optionHeader}>
                    <div className={styles.optionIcon}>
                      <LinkIcon size={20} />
                    </div>
                  <Tooltip content="Los profesionales tendrán acceso a todos los archivos de una vez">
                    <div className={styles.optionTitle}>
                      <h5>Opción 1: Carpeta Compartida</h5>
                      <p>Si tienes toda la documentación en una carpeta</p>
                    </div>
                  </Tooltip>
                </div>
                <div className={styles.optionContent}>
                  <input
                    id="subProjectLink"
                    type="url"
                    className={`${styles.input} ${validation.link ? styles.inputError : ''}`}
                    value={formData.link || ''}
                    onChange={(e) => handleInputChange('link', e.target.value)}
                    placeholder="https://drive.google.com/drive/folders/1ABC... (carpeta con todos los archivos)"
                    disabled={isLoading}
                  />
                  {validation.link && (
                    <span className={styles.errorText}>{validation.link}</span>
                  )}
                </div>
                </div>

                {/* Opción 2: Archivos individuales */}
                <div className={styles.optionCard}>
                  <div className={styles.optionHeader}>
                    <div className={styles.optionIcon}>
                      <FolderOpen size={20} />
                    </div>
                    <div className={styles.optionTitle}>
                      <h5>Opción 2: Archivos Individuales</h5>
                      <p>Si quieres subir archivos específicos uno por uno</p>
                    </div>
                  </div>
                  <div className={styles.optionContent}>
                    <div className={styles.infoBox}>
                      <p className={styles.infoText}>
                        <strong>Tip:</strong> Puedes usar ambas opciones. Por ejemplo, compartir una carpeta general y además subir archivos específicos para este rubro.
                      </p>
                    </div>
                    
                    <div className={styles.fileFields}>
                <div className={styles.fieldGroup}>
                  <Tooltip content="📋 Archivo específico para cotizar este rubro">
                    <label className={styles.label} htmlFor="plantillaPresupuesto">
                      <Upload size={18} />
                      Plantilla de Presupuesto
                    </label>
                  </Tooltip>
                  <div className={`${styles.fileInputWrapper} ${formData.plantillaDePresupuesto ? styles.fileInputWrapperUploaded : ''}`}>
                    <input
                      id="plantillaPresupuesto"
                      type="file"
                      accept=".pdf,.xlsx,.xls"
                      onChange={(e) => handleFileChange('plantillaDePresupuesto', e)}
                      className={styles.fileInputHidden}
                      disabled={isLoading}
                    />
                    {!formData.plantillaDePresupuesto && (
                      <label htmlFor="plantillaPresupuesto" className={styles.fileInputLabel}>
                        Seleccionar archivo
                      </label>
                    )}
                  </div>
                  {formData.plantillaDePresupuesto && (
                    <div className={styles.fileUploaded}>
                      <div className={styles.fileUploadedContent}>
                        {getFileIcon(formData.plantillaDePresupuesto.name)}
                        <span className={styles.fileName}>
                          {formData.plantillaDePresupuesto.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        className={styles.removeFileButton}
                        onClick={() => {
                          setFormData(prev => ({ ...prev, plantillaDePresupuesto: null }));
                          setIsDirty(true);
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>

                <div className={styles.fieldGroup}>
                  <Tooltip content="📐 Planos específicos para este rubro">
                    <label className={styles.label} htmlFor="planosArchivo">
                      <Upload size={18} />
                      Planos
                    </label>
                  </Tooltip>
                  <div className={`${styles.fileInputWrapper} ${formData.planosArchivo ? styles.fileInputWrapperUploaded : ''}`}>
                    <input
                      id="planosArchivo"
                      type="file"
                      accept=".pdf,.xlsx,.xls"
                      onChange={(e) => handleFileChange('planosArchivo', e)}
                      className={styles.fileInputHidden}
                      disabled={isLoading}
                    />
                    {!formData.planosArchivo && (
                      <label htmlFor="planosArchivo" className={styles.fileInputLabel}>
                        Seleccionar archivo
                      </label>
                    )}
                  </div>
                  {(formData.planosArchivo || formData.existingPlanos) && (
                    <div className={styles.fileUploaded}>
                      <div className={styles.fileUploadedContent}>
                        {formData.planosArchivo ? 
                          getFileIcon(formData.planosArchivo.name) : 
                          <FileIcon size={16} className={styles.fileIcon} />
                        }
                        <span className={styles.fileName}>
                          {formData.planosArchivo ? 
                            formData.planosArchivo.name : 
                            formData.existingPlanos?.name || 'Archivo cargado'
                          }
                        </span>
                        {formData.existingPlanos && (
                          <a 
                            href={formData.existingPlanos.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={styles.downloadLink}
                          >
                            <Download size={14} />
                          </a>
                        )}
                      </div>
                      <button
                        type="button"
                        className={styles.removeFileButton}
                        onClick={() => {
                          setFormData(prev => ({ 
                            ...prev, 
                            planosArchivo: null,
                            existingPlanos: null 
                          }));
                          setIsDirty(true);
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>

                <div className={styles.fieldGroup}>
                  <Tooltip content="📊 Cálculos y mediciones del proyecto">
                    <label className={styles.label} htmlFor="computoMetrico">
                      <Upload size={18} />
                      Cómputo Métrico
                    </label>
                  </Tooltip>
                  <div className={`${styles.fileInputWrapper} ${formData.computoMetrico ? styles.fileInputWrapperUploaded : ''}`}>
                    <input
                      id="computoMetrico"
                      type="file"
                      accept=".pdf,.xlsx,.xls"
                      onChange={(e) => handleFileChange('computoMetrico', e)}
                      className={styles.fileInputHidden}
                      disabled={isLoading}
                    />
                    {!formData.computoMetrico && (
                      <label htmlFor="computoMetrico" className={styles.fileInputLabel}>
                        Seleccionar archivo
                      </label>
                    )}
                  </div>
                  {(formData.computoMetrico || formData.existingComputoMetrico) && (
                    <div className={styles.fileUploaded}>
                      <div className={styles.fileUploadedContent}>
                        {formData.computoMetrico ? 
                          getFileIcon(formData.computoMetrico.name) : 
                          <FileIcon size={16} className={styles.fileIcon} />
                        }
                        <span className={styles.fileName}>
                          {formData.computoMetrico ? 
                            formData.computoMetrico.name : 
                            formData.existingComputoMetrico?.name || 'Archivo cargado'
                          }
                        </span>
                        {formData.existingComputoMetrico && (
                          <a 
                            href={formData.existingComputoMetrico.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={styles.downloadLink}
                          >
                            <Download size={14} />
                          </a>
                        )}
                      </div>
                      <button
                        type="button"
                        className={styles.removeFileButton}
                        onClick={() => {
                          setFormData(prev => ({ 
                            ...prev, 
                            computoMetrico: null,
                            existingComputoMetrico: null 
                          }));
                          setIsDirty(true);
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>

                <div className={styles.fieldGroup}>
                  <Tooltip content="🔧 Detalles técnicos y especificaciones">
                    <label className={styles.label} htmlFor="especificacionesTecnicas">
                      <Upload size={18} />
                      Especificaciones Técnicas
                    </label>
                  </Tooltip>
                  <div className={`${styles.fileInputWrapper} ${formData.especificacionesTecnicas ? styles.fileInputWrapperUploaded : ''}`}>
                    <input
                      id="especificacionesTecnicas"
                      type="file"
                      accept=".pdf,.xlsx,.xls"
                      onChange={(e) => handleFileChange('especificacionesTecnicas', e)}
                      className={styles.fileInputHidden}
                      disabled={isLoading}
                    />
                    {!formData.especificacionesTecnicas && (
                      <label htmlFor="especificacionesTecnicas" className={styles.fileInputLabel}>
                        Seleccionar archivo
                      </label>
                    )}
                  </div>
                  {(formData.especificacionesTecnicas || formData.existingEspecificacionesTecnicas) && (
                    <div className={styles.fileUploaded}>
                      <div className={styles.fileUploadedContent}>
                        {formData.especificacionesTecnicas ? 
                          getFileIcon(formData.especificacionesTecnicas.name) : 
                          <FileIcon size={16} className={styles.fileIcon} />
                        }
                        <span className={styles.fileName}>
                          {formData.especificacionesTecnicas ? 
                            formData.especificacionesTecnicas.name : 
                            formData.existingEspecificacionesTecnicas?.name || 'Archivo cargado'
                          }
                        </span>
                        {formData.existingEspecificacionesTecnicas && (
                          <a 
                            href={formData.existingEspecificacionesTecnicas.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={styles.downloadLink}
                          >
                            <Download size={14} />
                          </a>
                        )}
                      </div>
                      <button
                        type="button"
                        className={styles.removeFileButton}
                        onClick={() => {
                          setFormData(prev => ({ 
                            ...prev, 
                            especificacionesTecnicas: null,
                            existingEspecificacionesTecnicas: null 
                          }));
                          setIsDirty(true);
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={styles.saveButton}
              onClick={handleSave}
              disabled={isLoading}
            >
              <Save size={16} />
              {isLoading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </motion.div>
    </ModalOverlay>
  );
};

const mapStateToProps = (state: any) => {
  return {};
};

export default connect(mapStateToProps, {
  openMainModal,
  closeMainModal
})(NewSubProjectModalV2);
